import {
  all,
  fork,
  takeLatest,
  put,
  select,
  call,
  delay,
} from 'typed-redux-saga/macro';
import { redirect } from 'redux-first-router';

import {
  fetchFolders,
  FETCH_FOLDERS_SUCCESS,
  FETCH_SUB_FOLDERS_REQUEST,
  getSelectedFolderId,
  getRootFolderId,
  setSelectedFolderId,
  ON_FETCH_FOLDERS,
  FETCH_CREATE_FOLDER_REQUEST,
  fetchCreateFolder,
  FETCH_CREATE_FOLDER_SUCCESS,
  FETCH_UPDATE_FOLDER_REQUEST,
  fetchUpdateFolder,
  FETCH_UPDATE_FOLDER_SUCCESS,
  FETCH_MOVE_SUB_FOLDERS_REQUEST,
  FETCH_MOVE_SUB_FOLDERS,
  FETCH_MOVE_SUB_FOLDERS_SUCCESS,
  FETCH_MOVE_SUB_FOLDERS_FAILURE,
  setSelectedMoveFolderId,
  getSelectedMoveFolderId,
  FETCH_DELETE_FOLDER_REQUEST,
  fetchDeleteFolder,
  getFolders,
  FETCH_DELETE_FOLDER_SUCCESS,
  FETCH_DELETE_FOLDER_FAILURE,
  FETCH_CREATE_FOLDER_FAILURE,
  FETCH_UPDATE_FOLDER_FAILURE,
  FETCH_UPDATE_MOVE_FOLDER_REQUEST,
  FETCH_UPDATE_MOVE_FOLDER_SUCCESS,
  FETCH_UPDATE_MOVE_FOLDER_FAILURE,
  getMoveFolders,
  fetchMoveFolder,
  FETCH_MOVE_FOLDER_REQUEST,
  FETCH_MOVE_FOLDER,
  FETCH_MOVE_FOLDER_SUCCESS,
  FETCH_MOVE_FOLDER_FAILURE,
  createMoveFilesRequest,
  FETCH_UPDATE_MOVE_FILES_REQUEST,
  FETCH_UPDATE_MOVE_FILES_SUCCESS,
  FETCH_UPDATE_MOVE_FILES_FAILURE,
  createDeleteFilesRequest,
  DELETE_FILES_REQUEST,
  DELETE_FILES_SUCCESS,
  DELETE_FILES_FAILURE,
  getActionFolderId,
  getOpenFolderId,
  getCaseOptions,
  SET_SELECTED_FOLDER_ID,
  FETCH_SUB_FOLDERS_SUCCESS,
  HIDE_FOLDER,
  getDispatcherComponent,
  FETCH_FOLDERS,
  FETCH_FOLDERS_FAILURE,
  FETCH_SUB_FOLDERS,
  FETCH_SUB_FOLDERS_FAILURE,
  onFetchFolders,
  FETCH_CHILD_FOLDER_SUCCESS,
  createFetchChildFoldersRequest,
  FETCH_CHILD_FOLDER_REQUEST,
  createFetchFolderFromRootRequest,
  SET_V2_FOLDERS_ENABLED,
  getV2FoldersEnabled,
  resolveFolderMutationId,
  orderFolderPathFromRoot,
  getUrlFolderId,
  CLEAR_FOLDER_DISPATCHER_COMPONENT,
} from '.';
import {
  ROUTE_TABS,
  ROUTE_FILE,
  buildFolderRouteAction,
  selectCurrentRoutePayload,
  selectCurrentRouteQuery,
  selectRouteFolderId,
  selectRouteType,
} from '../routing';
import {
  getTdosForExport,
  unselectRows,
  DELETED_TDOS,
  MoveFolderData,
  MOVED_TDOS,
} from 'state/modules/tdosTable';
import {
  getSearchResultTdosAll,
  updateSearchResultTdos,
  updateSearchByFolderQuery,
  getFullSearchQuery,
  searchMedia,
  getCurrentPage,
  getLimit,
  getTotalResults,
  UPDATE_CURRENT_PAGE,
} from 'state/modules/search';
import { showNotification } from '../snackbar';
import { get, isEmpty, isString } from 'lodash';
import { FETCH_FOLDER_UPLOAD_REQUEST } from '../uploadFile/actions';
import { handleRequest } from '../../utils/util';
import { Action } from 'state/sagas';
import { AsyncFuncActions } from '~helpers/apiHelper';
import { TDO } from '../universal/TDO';
import { IN_REDACTION } from 'state/modules/sendToRedact';
const ROOT_FOLDER_TYPE = 'cms';

function* doFetchFolders() {
  // VE-21116: sync the org-level Folders v2 flag into the slice BEFORE the
  // tree is fetched, so the reducers' folderPath-based level math uses the
  // right Folders version (the reducer cannot read root state itself).
  const isV2Folders = yield* select(getV2FoldersEnabled);
  yield* put(SET_V2_FOLDERS_ENABLED(isV2Folders));
  yield* fetchMoreFolders(
    'root',
    [FETCH_FOLDERS, FETCH_FOLDERS_SUCCESS, FETCH_FOLDERS_FAILURE],
    null,
    {}
  );
}

function* watchFetchFolders() {
  yield* all([takeLatest(ON_FETCH_FOLDERS, doFetchFolders)]);
}

function* watchFetchFoldersSuccess() {
  yield* all([takeLatest(FETCH_FOLDERS_SUCCESS, selectInitialFolder)]);
}

function* selectInitialFolder() {
  const selectedFolderId = yield* select(getSelectedFolderId);
  const rootFolderId = yield* select(getRootFolderId);
  const selectedMoveFolderId = yield* select(getSelectedMoveFolderId);
  // VE-27161: the folder the user was in is carried in the URL. This runs on
  // FETCH_FOLDERS_SUCCESS rather than on the route action because the initial
  // route is dispatched before boot, when the tree has not been fetched yet.
  const routeFolderId = yield* select(selectRouteFolderId);

  if (!selectedFolderId) {
    if (routeFolderId && routeFolderId !== rootFolderId) {
      yield* restoreFolderFromUrl(routeFolderId);
    } else {
      yield* put(setSelectedFolderId(rootFolderId, 'initialPageLoad'));
    }
  }
  if (!selectedMoveFolderId) {
    yield* put(setSelectedMoveFolderId(rootFolderId));
  }
}

interface FolderPathResponse {
  folder?: {
    id: string;
    name: string;
    folderPath?: { id: string; name?: string }[];
  } | null;
}

// VE-27161: open every folder from the root down to `folderId` so the sidebar
// tree is expanded along that branch and the breadcrumbs are rebuilt, then
// select it. Only ONE select is dispatched, at the end, so the folder change
// triggers a single search (see watchSelectedFolder in modules/search/sagas).
//
// Every selection here is tagged 'initialPageLoad': the URL already names the
// folder, so watchSelectedFolder in modules/tdosTable must not re-dispatch
// ROUTE_TABS -- that would close a file preview being restored and push a
// history entry in the middle of a Back/Forward.
export function* restoreFolderFromUrl(folderId: string): any {
  const rootFolderId = yield* select(getRootFolderId);
  // The folder fetches below must not be mistaken for a breadcrumb navigation
  // (breadcrumbsFetchData): after a breadcrumb click followed by Back, each
  // ancestor opened would be re-selected mid-walk, firing ROUTE_TABS -- which
  // closes a restored preview and cancels this restore.
  yield* put(CLEAR_FOLDER_DISPATCHER_COMPONENT());

  if (!folderId || folderId === rootFolderId) {
    yield* fetchMoreFolders(
      'root',
      [FETCH_FOLDERS, FETCH_FOLDERS_SUCCESS, FETCH_FOLDERS_FAILURE],
      null,
      {}
    );
    yield* put(setSelectedFolderId(rootFolderId, 'initialPageLoad'));
    return;
  }

  const queryFolderPath = `query folder($id: ID!) {
    folder(id: $id) {
      id
      name
      folderPath{
        id
        name
      }
    }
  }`;
  const { error, response } = (yield* call(handleRequest<FolderPathResponse>, {
    query: queryFolderPath,
    variables: { id: folderId },
  })) as {
    error?: unknown;
    response?: { data?: FolderPathResponse };
  };
  const folder = response?.data?.folder;

  if (error || !folder) {
    // Deleted folder, revoked access or a hand-edited id: fall back to the root
    // folder. The URL watcher then drops the stale id from the address bar.
    console.warn(`Unable to restore folder ${folderId} from the URL`, error);
    yield* put(setSelectedFolderId(rootFolderId, 'initialPageLoad'));
    return;
  }

  const foldersToOpen = [
    ...orderFolderPathFromRoot(folder.folderPath, rootFolderId, folderId),
    { id: folderId },
  ];

  // Sequential on purpose: handleFetchSelectSubfolder watches
  // FETCH_SUB_FOLDERS_REQUEST with takeLatest, so firing one request per
  // ancestor would cancel all but the last and leave the tree half expanded.
  // A failed fetch (network error, no access to an ancestor) throws out of
  // fetchFolderFromNonRoot; stop at the deepest folder that did open, so the
  // breadcrumbs built from openFolderIds still match the selection, instead of
  // letting the error take down the root saga.
  let deepestOpenedId = rootFolderId;
  try {
    for (const { id } of foldersToOpen) {
      yield* fetchMoreFolders(
        'subFolder',
        [
          FETCH_SUB_FOLDERS,
          FETCH_SUB_FOLDERS_SUCCESS,
          FETCH_SUB_FOLDERS_FAILURE,
        ],
        id,
        { folderId: id, expanded: true, type: null }
      );
      deepestOpenedId = id;
    }
  } catch (walkError) {
    console.warn(`Unable to open folder ${folderId} from the URL`, walkError);
  }

  yield* put(setSelectedFolderId(deepestOpenedId, 'initialPageLoad'));
}

// VE-27161: mirror the selected folder into the URL. Centralised here so every
// way of changing folder is covered -- sidebar, breadcrumbs, and the sagas that
// move the user after a folder is deleted or moved.
export function* syncSelectedFolderToUrl() {
  const routeType = yield* select(selectRouteType);
  if (![ROUTE_TABS.type, ROUTE_FILE.type].includes(routeType)) {
    return;
  }

  const routeFolderId = yield* select(selectRouteFolderId);
  const nextFolderId = yield* select(getUrlFolderId);

  if (nextFolderId === routeFolderId) {
    return;
  }

  const payload = yield* select(selectCurrentRoutePayload);
  const query = yield* select(selectCurrentRouteQuery);
  // Re-stamping the URL must not disturb the files table: watchRouteTabs turns
  // any ROUTE_TABS into RESET_DATA_PREVIEW, which wipes the row selection
  // unless isClosePreview is set. Nothing is actually closed here -- a preview
  // can only be open on the file route, which keeps its own payload.
  const nextPayload =
    routeType === ROUTE_TABS.type
      ? { ...payload, isClosePreview: true }
      : payload;
  // redirect() replaces the current history entry: changing folder is not a
  // navigation the Back button should have to step through.
  yield* put(
    redirect(
      buildFolderRouteAction(routeType, nextPayload, query, nextFolderId)
    )
  );
}

function* watchSyncFolderToUrl() {
  yield* takeLatest(SET_SELECTED_FOLDER_ID, function* (action) {
    // 'moveFolder' selects the destination of a move dialog, not the folder the
    // user is browsing -- the reducer ignores it too.
    if (get(action, 'payload.type') === 'moveFolder') {
      return;
    }
    yield* syncSelectedFolderToUrl();
  });
}

// VE-27161: keep the URL and the selected folder in step once the app is
// running. Which side wins is decided by how the navigation happened:
// redux-first-router tags browser Back/Forward as 'pop' (and 'back'/'next'),
// everything the app dispatches itself as 'push'/'redirect'.
const URL_DRIVEN_NAVIGATION = /back|next|pop/;

export function* handleRouteFolderQuery(action: unknown) {
  const kind = get(action, 'meta.location.kind', '');
  // 'load' is the initial route dispatch, before the tree exists.
  if (!kind || kind === 'load') {
    return;
  }

  const rootFolderId = yield* select(getRootFolderId);
  const selectedFolderId = yield* select(getSelectedFolderId);
  if (!rootFolderId || !selectedFolderId) {
    // Still booting: selectInitialFolder owns the first selection.
    return;
  }

  const routeFolderId = yield* select(selectRouteFolderId);
  if ((routeFolderId || rootFolderId) === selectedFolderId) {
    return;
  }

  if (URL_DRIVEN_NAVIGATION.test(kind)) {
    yield* restoreFolderFromUrl(routeFolderId || rootFolderId);
  } else {
    // Tab switch, opening a file preview, ...: the folder must survive it.
    yield* syncSelectedFolderToUrl();
  }
}

function* watchRouteFolderQuery() {
  // takeLatest: on rapid Back/Forward only the last entry's folder is restored,
  // rather than several restores interleaving their fetches and selections.
  yield* takeLatest([ROUTE_TABS, ROUTE_FILE], handleRouteFolderQuery);
}

export function* handleFetchSelectSubfolder() {
  yield* takeLatest(FETCH_SUB_FOLDERS_REQUEST, function* (action) {
    const rootFolderId = yield* select(getRootFolderId);
    if (rootFolderId === action.payload.folderId) {
      yield* fetchMoreFolders(
        'root',
        [FETCH_FOLDERS, FETCH_FOLDERS_SUCCESS, FETCH_FOLDERS_FAILURE],
        null,
        {}
      );
    } else {
      const variables = {
        folderId: action.payload.folderId,
        expanded: action.payload.expanded,
        type: null,
      };
      yield* fetchMoreFolders(
        'subFolder',
        [
          FETCH_SUB_FOLDERS,
          FETCH_SUB_FOLDERS_SUCCESS,
          FETCH_SUB_FOLDERS_FAILURE,
        ],
        action.payload.folderId,
        variables
      );
    }
  });
}
function* handleFetchCreateFolder() {
  yield* all([takeLatest(FETCH_CREATE_FOLDER_REQUEST, createFolder)]);
  yield* takeLatest(
    [FETCH_CREATE_FOLDER_SUCCESS, FETCH_CREATE_FOLDER_FAILURE],
    function* (action) {
      if (FETCH_CREATE_FOLDER_SUCCESS.match(action)) {
        const rootFolderId = yield* select(getRootFolderId);
        const parentFolderId = action.meta.variables.parentFolderId;
        if (rootFolderId === parentFolderId) {
          yield* fetchMoreFolders(
            'root',
            [FETCH_FOLDERS, FETCH_FOLDERS_SUCCESS, FETCH_FOLDERS_FAILURE],
            null,
            {}
          );
        } else {
          const variables = {
            folderId: parentFolderId,
            expanded: true,
            type: null,
          };
          yield* fetchMoreFolders(
            'subFolder',
            [
              FETCH_SUB_FOLDERS,
              FETCH_SUB_FOLDERS_SUCCESS,
              FETCH_SUB_FOLDERS_FAILURE,
            ],
            parentFolderId,
            variables
          );
        }
        yield* put(
          showNotification(
            `New Folder: ${action.payload.createFolder.name} has been created`,
            'success'
          )
        );
      } else if (FETCH_CREATE_FOLDER_FAILURE.match(action)) {
        let message = 'Cannot create new folder ';
        if (action.payload?.[0]?.data?.maxAllowedDepth) {
          message +=
            '(Adding the new folder would exceed maximum folder depth limitations)';
        }
        yield* put(showNotification(message, 'error'));
      }
    }
  );
}
function* createFolder(action: Action<string>) {
  const openFolderId = yield* select(getOpenFolderId);
  yield* put(fetchCreateFolder(action.payload, openFolderId));
}
function* handleFetchUpdateFolder() {
  yield* all([takeLatest(FETCH_UPDATE_FOLDER_REQUEST, updateFolder)]);
  yield* takeLatest(
    [FETCH_UPDATE_FOLDER_SUCCESS, FETCH_UPDATE_FOLDER_FAILURE],
    function* (action) {
      if (FETCH_UPDATE_FOLDER_SUCCESS.match(action)) {
        yield* put(
          showNotification(
            `Folder has been renamed to: ${action.payload.updateFolder.name}`,
            'success'
          )
        );
      } else if (FETCH_UPDATE_FOLDER_FAILURE.match(action)) {
        yield* put(
          showNotification(
            `Cannot rename folder ${action.meta.variables.name}`,
            'error'
          )
        );
      }
    }
  );
}
function* updateFolder(action: Action<{ id: string; name: string }>) {
  yield* put(fetchUpdateFolder(action.payload.id, action.payload.name));
}

export function* handleFetchMoveFolders() {
  yield* takeLatest(FETCH_MOVE_FOLDER_REQUEST, function* () {
    yield* fetchMoreFolders(
      'root',
      [FETCH_MOVE_FOLDER, FETCH_MOVE_FOLDER_SUCCESS, FETCH_MOVE_FOLDER_FAILURE],
      null,
      {}
    );
  });
}
export function* handleFetchSelectMoveSubfolder() {
  yield* takeLatest(FETCH_MOVE_SUB_FOLDERS_REQUEST, function* (action) {
    const rootFolderId = yield* select(getRootFolderId);
    if (rootFolderId === action.payload.folderId) {
      yield* fetchMoreFolders(
        'root',
        [
          FETCH_MOVE_FOLDER,
          FETCH_MOVE_FOLDER_SUCCESS,
          FETCH_MOVE_FOLDER_FAILURE,
        ],
        null,
        {}
      );
    } else {
      const variables = {
        folderId: action.payload.folderId,
        expanded: action.payload.expanded,
        type: null,
      };
      yield* fetchMoreFolders(
        'subFolder',
        [
          FETCH_MOVE_SUB_FOLDERS,
          FETCH_MOVE_SUB_FOLDERS_SUCCESS,
          FETCH_MOVE_SUB_FOLDERS_FAILURE,
        ],
        action.payload.folderId,
        variables
      );
    }
  });
}
function* handleFetchDeleteFolder() {
  yield* all([takeLatest(FETCH_DELETE_FOLDER_REQUEST, deleteFolder)]);
}
function* deleteFolder() {
  const selectedFolderId = yield* select(getSelectedFolderId);
  const folders = yield* select(getFolders);
  const selectedFolder = folders[selectedFolderId];
  if (!selectedFolder) {
    yield* put(showNotification(`Unable to find the selected folder`, 'error'));
    return;
  }

  const treeObjectId = selectedFolder.treeObjectId;
  const parentFolderId = selectedFolder.parentId;
  if (!parentFolderId) {
    yield* put(showNotification(`Cannot delete root folder`, 'error'));
    return;
  }

  // VE-21116: under Folders v2, deleteFolder is a HARD delete that rejects
  // non-empty folders (v1 soft-deleted regardless). Pre-check the known
  // sub-folder count for a clear message; contained files aren't tracked in
  // this slice, so the surfaced API error below covers that case.
  const isV2Folders = yield* select(getV2FoldersEnabled);
  if (isV2Folders && (selectedFolder.count ?? 0) > 0) {
    yield* put(
      showNotification(
        `Folder: ${selectedFolder.name} must be empty to delete — remove its contents first`,
        'error'
      )
    );
    return;
  }

  const subfolders = folders[parentFolderId]?.subfolders || [selectedFolderId];
  const rootFolderId = yield* select(getRootFolderId);
  yield* put(fetchDeleteFolder(treeObjectId, selectedFolderId));
  yield* takeLatest(
    [FETCH_DELETE_FOLDER_SUCCESS, FETCH_DELETE_FOLDER_FAILURE],
    function* (action) {
      if (FETCH_DELETE_FOLDER_SUCCESS.match(action)) {
        if (rootFolderId === parentFolderId) {
          yield* fetchMoreFolders(
            'root',
            [FETCH_FOLDERS, FETCH_FOLDERS_SUCCESS, FETCH_FOLDERS_FAILURE],
            null,
            {}
          );
        } else {
          const variables = {
            folderId: parentFolderId,
            expanded: subfolders.length > 1 ? true : false,
            type: null,
          };
          yield* fetchMoreFolders(
            'subFolder',
            [
              FETCH_SUB_FOLDERS,
              FETCH_SUB_FOLDERS_SUCCESS,
              FETCH_SUB_FOLDERS_FAILURE,
            ],
            parentFolderId,
            variables
          );
        }
        yield* put(setSelectedFolderId(parentFolderId));
        yield* put(
          showNotification(
            `Folder: ${folders[selectedFolderId]?.name} has been deleted`,
            'success'
          )
        );
      } else if (FETCH_DELETE_FOLDER_FAILURE.match(action)) {
        // VE-21116: surface the real API error (e.g. v2's "folder not empty")
        // instead of the generic message when one is available.
        const apiMessage = get(action, 'payload[0].message');
        yield* put(
          showNotification(
            isString(apiMessage) && apiMessage.trim()
              ? `Cannot delete ${selectedFolder.name}: ${apiMessage.trim()}`
              : `Cannot delete ${selectedFolder.name}`,
            'error'
          )
        );
      }
    }
  );
}

export function* hanldeFetchMoveSubFolder() {
  yield* all([takeLatest(FETCH_UPDATE_MOVE_FOLDER_REQUEST, moveFolder)]);
}
function* moveFolder() {
  const caseOptions = yield* select(getCaseOptions);
  const actionFolderId =
    caseOptions === 'tab'
      ? yield* select(getSelectedFolderId)
      : yield* select(getActionFolderId);
  const folders = yield* select(getFolders);
  const treeObjectId = folders[actionFolderId]?.treeObjectId ?? '';
  const selectedMoveFolderId = yield* select(getSelectedMoveFolderId);
  const moveFolder = yield* select(getMoveFolders);
  const treeObjectNewParentId =
    moveFolder[selectedMoveFolderId]?.treeObjectId ?? '';
  const parentFolderId = folders[actionFolderId]?.parentId ?? '';
  const treeObjectPrevParent = folders[parentFolderId]?.treeObjectId ?? '';
  const rootFolderId = yield* select(getRootFolderId);
  const treeObjectIdRoot = folders[rootFolderId]?.treeObjectId;
  // VE-21116: v2 authorizes moveFolder by the folder ids; v1 keeps passing the
  // treeObject ids as the mutation's input values.
  const isV2Folders = yield* select(getV2FoldersEnabled);
  yield* put(
    fetchMoveFolder(
      resolveFolderMutationId(isV2Folders, {
        folderId: actionFolderId,
        treeObjectId,
      }),
      resolveFolderMutationId(isV2Folders, {
        folderId: selectedMoveFolderId,
        treeObjectId: treeObjectNewParentId,
      }),
      resolveFolderMutationId(isV2Folders, {
        folderId: parentFolderId,
        treeObjectId: treeObjectPrevParent,
      })
    )
  );
  yield* takeLatest(
    [FETCH_UPDATE_MOVE_FOLDER_SUCCESS, FETCH_UPDATE_MOVE_FOLDER_FAILURE],
    function* (action) {
      if (FETCH_UPDATE_MOVE_FOLDER_SUCCESS.match(action)) {
        if (treeObjectIdRoot === treeObjectNewParentId) {
          yield* fetchMoreFolders(
            'root',
            [FETCH_FOLDERS, FETCH_FOLDERS_SUCCESS, FETCH_FOLDERS_FAILURE],
            null,
            { type: 'moveFolder' }
          );
        } else {
          const variables = {
            folderId: selectedMoveFolderId,
            expanded: true,
            type: 'moveFolder',
          };
          yield* fetchMoreFolders(
            'subFolder',
            [
              FETCH_SUB_FOLDERS,
              FETCH_SUB_FOLDERS_SUCCESS,
              FETCH_SUB_FOLDERS_FAILURE,
            ],
            selectedMoveFolderId,
            variables
          );
        }

        yield* put(
          showNotification(
            `Folder ${action.payload.moveFolder.name} has been moved`,
            'success'
          )
        );

        const selectedFolderId = yield* select(getSelectedFolderId);
        yield* put(
          SET_SELECTED_FOLDER_ID({
            selectedFolderId,
            type: 'moveFolder',
          })
        );
      } else if (FETCH_UPDATE_MOVE_FOLDER_FAILURE.match(action)) {
        yield* put(
          showNotification(
            `Cannot move ${folders[actionFolderId]?.name}`,
            'error'
          )
        );
      }
    }
  );
}
export function* handleFetchMoveFiles() {
  yield* all([takeLatest(FETCH_UPDATE_MOVE_FILES_REQUEST, moveFiles)]);
}
function* moveFiles() {
  const selectedMoveFolderId = yield* select(getSelectedMoveFolderId);
  const selectedFolderId = yield* select(getSelectedFolderId);

  const moveFolder = yield* select(getMoveFolders);
  const treeObjectNewId = moveFolder[selectedMoveFolderId]?.treeObjectId ?? '';
  const selectedTdoIds = yield* select(getTdosForExport);
  const allTdos = yield* select(getSearchResultTdosAll);
  const allFolders = yield* select(getFolders);
  const currentFolder = allFolders[selectedFolderId];
  const currentFolderTreeObjectId = currentFolder?.treeObjectId ?? '';
  const errorTdoIds = allTdos
    .filter((tdo) => tdo.status === 'error')
    .map((tdo) => tdo.id);
  const selectedValidTdoIds = selectedTdoIds.filter(
    (id) => !errorTdoIds.includes(id)
  );

  const { tdosInRedaction, tdosNotInRedaction } = groupTdosByRedaction(
    allTdos,
    selectedValidTdoIds,
    selectedMoveFolderId,
    selectedFolderId
  );
  const isV2FoldersForMoveFiles = yield* select(getV2FoldersEnabled);

  if (!tdosNotInRedaction.length) {
    yield* put(
      showNotification(
        `File${tdosInRedaction.length > 1 ? 's' : ''} currently in redaction cannot be moved`,
        'error'
      )
    );
    return;
  }
  yield* put(
    createMoveFilesRequest(
      tdosNotInRedaction,
      // VE-21116: v2 authorizes moveTemporalDataObject's folder inputs by the
      // folder ids; v1 keeps the treeObject ids.
      resolveFolderMutationId(isV2FoldersForMoveFiles, {
        folderId: selectedMoveFolderId,
        treeObjectId: treeObjectNewId,
      }),
      resolveFolderMutationId(isV2FoldersForMoveFiles, {
        folderId: selectedFolderId,
        treeObjectId: currentFolderTreeObjectId,
      })
    )
  );
  yield* takeLatest(
    [FETCH_UPDATE_MOVE_FILES_SUCCESS, FETCH_UPDATE_MOVE_FILES_FAILURE],
    function* (action) {
      if (FETCH_UPDATE_MOVE_FILES_SUCCESS.match(action)) {
        const fileCountInRedaction = `${tdosInRedaction.length} file${tdosInRedaction.length > 1 ? 's have' : ' has'}`;
        const fileCountNotInRedaction = `${tdosNotInRedaction.length} file${tdosNotInRedaction.length > 1 ? 's have' : ' has'}`;
        const successNotification = `${fileCountNotInRedaction} been moved to ${moveFolder[selectedMoveFolderId]?.name} folder${tdosInRedaction.length ? `, ${fileCountInRedaction} currently in redaction cannot be moved.` : '.'} All changes take up to 20 seconds to complete.`;

        yield* put(showNotification(successNotification, 'success'));
        // Remove selected tdo
        yield* put(
          updateSearchResultTdos(
            tdosNotInRedaction.map((item) => item.id),
            'moving'
          )
        );
        // Used to change the pageSize
        yield* put(MOVED_TDOS(tdosNotInRedaction));
        yield* put(unselectRows());
        yield* delay(20000);
        const currentQuery = yield* select(getFullSearchQuery);
        yield* put(searchMedia(currentQuery, 0));
      } else if (FETCH_UPDATE_MOVE_FILES_FAILURE.match(action)) {
        yield* put(showNotification(`Cannot Move Files`, 'error'));
      }
    }
  );
}

export function* handleDeleteFiles() {
  yield* all([takeLatest(DELETE_FILES_REQUEST, deleteFiles)]);
}

export function* onDeleteFilesResult(
  selectedTdoIdsValid: string[],
  selectedTdos: TDO[],
  action:
    | ReturnType<typeof DELETE_FILES_SUCCESS>
    | ReturnType<typeof DELETE_FILES_FAILURE>
) {
  if (DELETE_FILES_SUCCESS.match(action)) {
    const successNotification =
      selectedTdoIdsValid.length > 1
        ? `Files have been deleted. All changes take up to 20 seconds to complete.`
        : `File has been deleted. All changes take up to 20 seconds to complete.`;
    yield* put(showNotification(successNotification, 'success'));
    // Grey out and drop the count for the rows actually deleted
    yield* put(updateSearchResultTdos(selectedTdoIdsValid, 'deleting'));
    // Update the pageSize
    yield* put(DELETED_TDOS(selectedTdos));
    // Unselect rows after deletion is successful
    yield* put(unselectRows());
  } else if (DELETE_FILES_FAILURE.match(action)) {
    yield* put(
      showNotification(`Errors have occurred while deleting files`, 'error')
    );
  }
  // wait ~20s for backend consistency, then re-run the current search so the
  // deleted rows leave the list. This runs on failure too: a new delete
  // cancels a previous delete's still-pending refresh (outer takeLatest), so
  // if the new one fails, this is what clears the earlier delete's greyed-out
  // rows.
  yield* delay(20000);
  // getFullSearchQuery uses the current page offset (the 0 below only tags the
  // SEARCH_MEDIA_SUCCESS payload). If the delete emptied a trailing page, step
  // back to the last page that still has rows so the refresh isn't empty.
  const limit = Number(yield* select(getLimit));
  if (limit > 0) {
    const totalResults = yield* select(getTotalResults);
    const currentPage = yield* select(getCurrentPage);
    const lastPage = Math.max(0, Math.ceil(totalResults / limit) - 1);
    if (currentPage > lastPage) {
      yield* put(UPDATE_CURRENT_PAGE(lastPage));
    }
  }
  const currentQuery = yield* select(getFullSearchQuery);
  yield* put(searchMedia(currentQuery, 0));
}

function* deleteFiles() {
  const selectedTdoIds = yield* select(getTdosForExport);
  const allTdos = yield* select(getSearchResultTdosAll);
  const selectedTdos = allTdos.filter(
    (tdo) => selectedTdoIds.includes(tdo.id) && tdo.status !== 'error'
  );
  const selectedTdoIdsValid = selectedTdos.map((tdo) => tdo.id);
  yield* put(createDeleteFilesRequest(selectedTdoIdsValid));
  yield* takeLatest(
    [DELETE_FILES_SUCCESS, DELETE_FILES_FAILURE],
    function* (action) {
      yield* onDeleteFilesResult(selectedTdoIdsValid, selectedTdos, action);
    }
  );
}

function* watchFetchSubFolders() {
  yield* takeLatest(FETCH_SUB_FOLDERS_SUCCESS, breadcrumbsFetchData);
  yield* takeLatest(FETCH_FOLDERS_SUCCESS, breadcrumbsFetchData);
}

function* breadcrumbsFetchData() {
  const fromComponent = yield* select(getDispatcherComponent);
  if (fromComponent === 'breadcrumbs') {
    yield* put(SET_SELECTED_FOLDER_ID());
    yield* put(HIDE_FOLDER());
  }
}

function* fetchMoreFolders(
  type: string,
  actionTypes: AsyncFuncActions<any>,
  folderId: string | null,
  variablesFolder: object
) {
  if (type === 'root') {
    return yield* put(
      createFetchFolderFromRootRequest(actionTypes, variablesFolder)
    );
  }
  return yield* fetchFolderFromNonRoot(actionTypes, folderId, variablesFolder);
}

function* fetchFolderFromNonRoot(
  actionTypes: AsyncFuncActions<any>,
  folderId: string | null,
  variablesFolder: object
): any {
  let count = 0;
  let offset = 0;
  const pageSize = 1000;
  const query = `query folder($id:ID!, $offset: Int, $limit: Int){
    folder(id: $id) {
      id
      name
      ownerId
      treeObjectId
      description
      childFolders(offset: $offset, limit: $limit){
        count
        records {
          id,
          name
          treeObjectId
          description
          parent{
            id
          }
          childFolders{
            count
          }
          folderPath{
            id
            name
          }
        }
      }
      parent{
        id
      }
      folderPath{
        id
        name
      }
    }
  }`;

  const results = [];
  do {
    const variables = {
      id: folderId,
      limit: pageSize,
      offset: offset,
      ...variablesFolder,
    };
    const data = yield* put(fetchFolders(query, variables, actionTypes));
    const response = yield data;
    count = response?.folder?.childFolders?.count ?? 0;
    results.push(response?.folder);
    offset += pageSize;
  } while (count === pageSize);

  const subFolders = results.flatMap(
    (item) => item?.childFolders?.records ?? []
  );
  const dataFolder: Record<string, any> = {};
  dataFolder.folder = { ...results[0] };
  dataFolder.folder.childFolders.records = subFolders;
  dataFolder.folder.childFolders.count = subFolders.length;

  if (isString(actionTypes[1])) {
    yield* put({
      type: actionTypes[1],
      payload: dataFolder,
      meta: {
        variables: variablesFolder,
      },
    });
  } else {
    yield* put(actionTypes[1](dataFolder, { variables: variablesFolder }));
  }

  return dataFolder;
}

export function* checkAndCreateRootFolder() {
  const queryRootFolders = `query folder {
    rootFolders(type: ${ROOT_FOLDER_TYPE}) {
      id
      name
    }
  }`;
  const rootFoldersResp = yield* call(handleRequest, {
    query: queryRootFolders,
  });
  if (rootFoldersResp.error) {
    throw rootFoldersResp.error;
  }

  const rootFolders = get(rootFoldersResp, 'response.data.rootFolders', []);
  if (isEmpty(rootFolders)) {
    console.warn('missing root folder. creating...');
    const queryCreateRootFolders = `mutation {
        createRootFolders(rootFolderType: ${ROOT_FOLDER_TYPE}) {
          id,
          name
        }
      }`;
    const queryCreateRootFoldersResp = yield* call(handleRequest, {
      query: queryCreateRootFolders,
    });
    if (queryCreateRootFoldersResp.error) {
      console.error(
        'Unable to create root folder. Make sure the user has CMS Editor role. ',
        queryCreateRootFoldersResp.error
      );
      throw rootFoldersResp.error;
    }
  }

  return yield* put(onFetchFolders());
}

function* watchFetchFolderUpload() {
  yield* takeLatest(FETCH_FOLDER_UPLOAD_REQUEST, function* () {
    yield* fetchMoreFolders(
      'root',
      [FETCH_MOVE_FOLDER, FETCH_MOVE_FOLDER_SUCCESS, FETCH_MOVE_FOLDER_FAILURE],
      null,
      {}
    );
  });
}

function* watchFetchChildFolders() {
  yield* takeLatest(FETCH_CHILD_FOLDER_REQUEST, function* (action) {
    const folderId = get(action, 'payload.folderId', '');
    if (folderId) {
      yield* put(createFetchChildFoldersRequest(folderId));
    }
  });
}

function* watchFetchChildFoldersSuccess() {
  yield* takeLatest(FETCH_CHILD_FOLDER_SUCCESS, function* () {
    const folders = yield* select(getFolders);
    const rootFolderId = yield* select(getRootFolderId);
    const selectedFolderId = yield* select(getSelectedFolderId);
    const folderTreeObjectId: any = folders[selectedFolderId]?.treeObjectId;
    const isRoot = selectedFolderId === rootFolderId;
    yield* put(updateSearchByFolderQuery(folderTreeObjectId, isRoot));
  });
}

function groupTdosByRedaction(
  allTdos: TDO[],
  selectedTdoIds: string[],
  selectedMoveFolderId: string,
  selectedFolderId: string
) {
  const tdosInRedaction = [] as MoveFolderData[];
  const tdosNotInRedaction = [] as MoveFolderData[];

  for (const tdo of allTdos) {
    if (selectedTdoIds.includes(tdo.id)) {
      const selectedTdo = {
        ...tdo,
        moveFolderId: selectedMoveFolderId,
        oldFolderId: selectedFolderId,
      };
      const hasRedactionTag = selectedTdo.details?.tags?.some(
        (tag) => tag.value === IN_REDACTION
      );

      if (hasRedactionTag) {
        tdosInRedaction.push(selectedTdo);
      } else {
        tdosNotInRedaction.push(selectedTdo);
      }
    }
  }

  return { tdosInRedaction, tdosNotInRedaction };
}
export default function* folders() {
  yield* all([
    fork(watchFetchSubFolders),
    fork(watchFetchFolders),
    fork(watchFetchFoldersSuccess),
    fork(handleFetchSelectSubfolder),
    fork(handleFetchCreateFolder),
    fork(handleFetchUpdateFolder),
    fork(handleFetchSelectMoveSubfolder),
    fork(handleFetchDeleteFolder),
    fork(handleDeleteFiles),
    fork(hanldeFetchMoveSubFolder),
    fork(handleFetchMoveFiles),
    fork(handleFetchMoveFolders),
    fork(watchFetchFolderUpload),
    fork(watchFetchChildFolders),
    fork(watchFetchChildFoldersSuccess),
    fork(watchSyncFolderToUrl), // VE-27161
    fork(watchRouteFolderQuery), // VE-27161
  ]);
}
